import { LIST_STATE_STORAGE_PREFIX } from '../../../lib/listStatePersistence';
import type { AccAccountTreeNode } from '../accounts/useData';

export type AnalyticalLedgersSearchMode = 'levelName' | 'levelCode' | 'narrative';

export type AnalyticalLedgersPersistedState = {
  dateFrom: string;
  dateTo: string;
  periodId: string;
  allCompanies: boolean;
  selectedCompanyIds: number[];
  companyFilter: string;
  searchMode: AnalyticalLedgersSearchMode;
  searchTerm: string;
  collapsedIds: number[];
  selectedAccountId: number | null;
};

export function getAnalyticalLedgersStateStorageKey(): string {
  return `${LIST_STATE_STORAGE_PREFIX}/accounting/reports/analytical-ledgers`;
}

export function loadAnalyticalLedgersState(): AnalyticalLedgersPersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(getAnalyticalLedgersStateStorageKey());
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<AnalyticalLedgersPersistedState>;
    return {
      dateFrom: typeof p.dateFrom === 'string' ? p.dateFrom : '',
      dateTo: typeof p.dateTo === 'string' ? p.dateTo : '',
      periodId: typeof p.periodId === 'string' ? p.periodId : '',
      allCompanies: p.allCompanies === true,
      selectedCompanyIds: Array.isArray(p.selectedCompanyIds)
        ? p.selectedCompanyIds.filter((id): id is number => typeof id === 'number')
        : [],
      companyFilter: typeof p.companyFilter === 'string' ? p.companyFilter : '',
      searchMode:
        p.searchMode === 'levelCode' || p.searchMode === 'narrative' ? p.searchMode : 'levelName',
      searchTerm: typeof p.searchTerm === 'string' ? p.searchTerm : '',
      collapsedIds: Array.isArray(p.collapsedIds)
        ? p.collapsedIds.filter((id): id is number => typeof id === 'number')
        : [],
      selectedAccountId: typeof p.selectedAccountId === 'number' ? p.selectedAccountId : null,
    };
  } catch {
    return null;
  }
}

export function persistAnalyticalLedgersState(state: AnalyticalLedgersPersistedState): void {
  try {
    sessionStorage.setItem(getAnalyticalLedgersStateStorageKey(), JSON.stringify(state));
  } catch {
    // quota / private mode
  }
}

export function buildAccountParentMap(
  nodes: AccAccountTreeNode[],
  parentId: number | null = null,
  map: Map<number, number | null> = new Map(),
): Map<number, number | null> {
  for (const node of nodes) {
    map.set(node.id, parentId);
    if (node.children?.length) {
      buildAccountParentMap(node.children, node.id, map);
    }
  }
  return map;
}

export function uncollapsePathToAccount(
  collapsedIds: Set<number>,
  accountId: number,
  parentMap: Map<number, number | null>,
): Set<number> {
  const next = new Set(collapsedIds);
  let current: number | null | undefined = parentMap.get(accountId) ?? null;
  while (current != null) {
    next.delete(current);
    current = parentMap.get(current) ?? null;
  }
  return next;
}
